import { useEffect } from 'react'
import { Grid } from '@mui/material'
import { useFormikContext } from 'formik'
import { t } from 'i18next'
import * as Yup from 'yup'
import { InputField, CheckBoxField, AutoCompleteField } from 'src/components/shared/ui/form/fields'
import useNumberValidations from 'src/components/shared/ui/form/fields/hooks/useNumberValidations'
import { FormField } from 'src/enums/Form'
import {
  useGetMunicipios,
  useGetPais,
  useGetProvincias,
  useGetTipoVia
} from 'src/hooks/fetch/useCatalogoGeneralFilters'

import { TipoVia, Pais, Provincia, Municipio } from 'src/interfaces/Catalog'
import { RepresentanteLegal, Interviniente } from 'src/interfaces/EscritosLegales'

const commonAddressFieldsValidation = (tipoVias?: TipoVia[]) => ({
  nombreVia: Yup.string()
    .nullable()
    .max(100, 'El nombre de la vía no puede tener más de 100 caracteres')
    .when('tipoVia', {
      is: (value: TipoVia) => value !== null,
      then: Yup.string()
        .nullable()
        .required('Este campo es obligatorio')
    })
    .when(['bloque', 'escalera', 'piso', 'letra', 'pais'], {
      is: (bloque: string, escalera: string, piso: string, letra: string, pais: Pais) =>
        bloque || escalera || piso || letra || pais,
      then: Yup.string().required('Este campo es obligatorio')
    }),
  numero: Yup.string()
    .nullable()
    .max(5, 'El número no puede tener más de 5 caracteres')
    .when(['tipoVia', 'nombreVia', 'km'], {
      is: (tipoVia: TipoVia, nombreVia: string, km: string) =>
        Boolean(tipoVia !== null && nombreVia !== '' && (!km || km?.length === 0)),
      then: Yup.string()
        .nullable()
        .required('Este campo es obligatorio')
    })
    .when(['bloque', 'escalera', 'piso', 'letra', 'pais', 'km'], {
      is: (bloque: string, escalera: string, piso: string, letra: string, pais: Pais, km: string) =>
        Boolean((bloque || escalera || piso || letra || pais) && (!km || km?.length === 0)),
      then: Yup.string()
        .nullable()
        .required('Este campo es obligatorio')
    }),
  km: Yup.string()
    .nullable()
    .max(5, 'El kilómetro no puede tener más de 5 caracteres')
    .test('isNumeric', 'El campo debe ser numérico', value =>
      value ? /^[0-9]+$/.test(value) : true
    ),
  piso: Yup.string()
    .nullable()
    .max(3, 'El piso no puede tener más de 3 caracteres'),
  letra: Yup.string()
    .nullable()
    .max(3, 'La letra no puede tener más de 3 caracteres'),
  bloque: Yup.string()
    .nullable()
    .max(5, 'El bloque no puede tener más de 5 caracteres'),
  escalera: Yup.string()
    .nullable()
    .max(2, 'La escalera no puede tener más de 2 caracteres'),
  provincia: Yup.object()
    .nullable()
    .when('pais', {
      is: (pais: Pais) => pais !== null && pais.iso2 === 'ES',
      then: Yup.object()
        .nullable()
        .required('Este campo es obligatorio')
    }),
  municipio: Yup.object()
    .nullable()
    .when('pais', {
      is: (pais: Pais) => pais !== null && pais.iso2 === 'ES',
      then: Yup.object()
        .nullable()
        .required('Este campo es obligatorio')
    }),
  codigoPostal: Yup.string()
    .nullable()
    .max(5, 'El código postal no puede tener más de 5 caracteres')
    .when('pais', {
      is: (pais: Pais) => pais !== null && pais.iso2 === 'ES',
      then: Yup.string().required('Este campo es obligatorio')
    }),
  tipoVia: Yup.object()
    .nullable()
    .test('tipoVia-required', 'Este campo es obligatorio', function (value) {
      const { bloque, escalera, piso, letra, pais, nombreVia, numero } = this.parent
      if (bloque || escalera || piso || letra || pais || nombreVia || numero) {
        return value !== null && value !== undefined
      }
      return true
    })
    .test('tipoVia-valido', 'El tipo de vía no es válido', function (value) {
      if (!value || !tipoVias) {
        return true
      }
      return tipoVias.some(tipoVia => tipoVia.codigo === (value as unknown as TipoVia).codigo)
    }),
  ciudadLocalidad: Yup.string()
    .nullable()
    .max(70, 'La dirección en el extranjero no puede tener más de 70 caracteres')
    .test('ciudadLocalidad-required', 'Este campo es obligatorio', function (value) {
      const { tipoVia, nombreVia, numero, km, bloque, escalera, piso, letra, pais } = this.parent
      const isNonES = pais !== null && pais !== undefined && pais.iso2 !== 'ES'
      const hasAddressContent = tipoVia || nombreVia || numero || km || bloque || escalera || piso || letra
      const paisIsNull = pais === null || pais === undefined
      if (isNonES || (hasAddressContent && paisIsNull)) {
        return Boolean(value)
      }
      return true
    }),
  informacionComplementaria: Yup.string()
    .nullable()
    .max(200, 'La información complementaria no puede tener más de 200 caracteres')
})

export const addressValidationSchema = (tipoVias?: TipoVia[]) => ({
  [FormField.INTERVINIENTE_DIRECCION]: Yup.object().shape({
    ...commonAddressFieldsValidation(tipoVias),
    pais: Yup.object()
      .nullable()
      .test('pais-required', 'Este campo es obligatorio', function (value) {
        const { tipoVia, nombreVia, numero, km, bloque, escalera, piso, letra, ciudadLocalidad } = this.parent
        if (tipoVia || nombreVia || numero || km || bloque || escalera || piso || letra || ciudadLocalidad) {
          return value !== null && value !== undefined
        }
        return true
      })
  })
})

export const addressValidationMonitorioSchema = (tipoVias?: TipoVia[]) => ({
  [FormField.INTERVINIENTE_DIRECCION]: Yup.object().shape({
    ...commonAddressFieldsValidation(tipoVias),
    pais: Yup.object()
      .nullable()
      .required('Este campo es obligatorio')
  })
})

export default function AddressForm(): JSX.Element {
  const tipoViaData = useGetTipoVia(FormField.INTERVINIENTE_DIRECCION_TIPO_VIA)
  const paisData = useGetPais(FormField.INTERVINIENTE_DIRECCION_PAIS)
  const { values, setFieldValue, validateForm } = useFormikContext<
    Interviniente | RepresentanteLegal
  >()
  const pais: Pais | null = (values && values.direccion && values.direccion.pais) || null
  const provinciasData: Provincia[] =
    useGetProvincias(FormField.TRASLADO_COPIAS_PROVINCIA, null) || []

  const { allowInteger } = useNumberValidations()

  const provincia: Provincia | null | undefined = values.direccion
    ? values.direccion.provincia
    : null
  const municipiosData: Municipio[] =
    useGetMunicipios(FormField.INTERVINIENTE_DIRECCION_MUNICIPIO, provincia) || []

  useEffect(() => {
    if (values.direccion?.sinNumero || values.direccion?.numero === 'SN') {
      setFieldValue('direccion.numero', values.direccion?.sinNumero ? 'SN' : '')
    }
  }, [values.direccion?.sinNumero, values.direccion?.numero, setFieldValue])

  useEffect(() => {
    const tipoVia = values.direccion?.tipoVia
    if (!tipoViaData || !tipoVia) {
      return
    }
    const tipoViaCatalogo = tipoViaData.find(item => item.codigo === tipoVia.codigo)
    if (!tipoViaCatalogo) {
      setFieldValue('direccion.tipoVia', null)
    } else if (tipoViaCatalogo.descripcion !== tipoVia.descripcion) {
      setFieldValue('direccion.tipoVia', tipoViaCatalogo)
    }
  }, [tipoViaData, values.direccion?.tipoVia, setFieldValue])

  function handleOnChange() {
    setFieldValue('direccion.municipio', null, false)
    setFieldValue('direccion.provincia', null, false)
    setFieldValue('direccion.codigoPostal', '', false)
    setFieldValue('direccion.ciudadLocalidad', '', false)
    setTimeout(() => validateForm)
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <AutoCompleteField
          id={FormField.INTERVINIENTE_DIRECCION_PAIS}
          label={t('form.field.pais')}
          items={paisData}
          itemName="nombre"
          onChange={handleOnChange}
        />
      </Grid>
      {pais?.iso2 === 'ES' && (
        <>
          <Grid item xs={12} sm={4}>
            <AutoCompleteField
              id={FormField.INTERVINIENTE_DIRECCION_PROVINCIA}
              items={provinciasData}
              label={t('form.field.provincia')}
              itemName="descripcion"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <AutoCompleteField
              id={FormField.INTERVINIENTE_DIRECCION_MUNICIPIO}
              items={municipiosData}
              label={t('form.field.municipio')}
              itemName="descripcion"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InputField
              id={FormField.INTERVINIENTE_DIRECCION_COD_POSTAL}
              label={t('form.field.codigo_postal')}
              maxLength={5}
              onChange={allowInteger}
            />
          </Grid>
        </>
      )}
      {pais?.iso2 === null ||
        (pais?.iso2 !== 'ES' && (
          <Grid item xs={12} sm={4}>
            <InputField
              id={FormField.INTERVINIENTE_DIRECCION_CIUDAD_LOCALIDAD}
              label={t('form.field.cuidadLocalidad')}
              maxLength={70}
            />
          </Grid>
        ))}
      <Grid item xs={12} sm={4}>
        <AutoCompleteField
          id={FormField.INTERVINIENTE_DIRECCION_TIPO_VIA}
          label={t('form.field.tipo_via')}
          items={tipoViaData}
          itemName="descripcion"
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_NOMBRE_VIA}
          label={t('form.field.nombre_via')}
          maxLength={100}
        />
      </Grid>

      <Grid item xs={6} sm={2}>
        <CheckBoxField id={FormField.INTERVINIENTE_DIRECCION_SN} label={t('form.field.s_n')} />
      </Grid>
      <Grid item xs={6} sm={2} justifyContent="center">
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_NUMERO}
          label={t('form.field.numero_direccion')}
          disabled={values.direccion?.sinNumero}
          maxLength={5}
        />
      </Grid>
      <Grid item xs={6} sm={2} justifyContent="center">
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_KM}
          label={t('form.field.km')}
          maxLength={3}
        />
      </Grid>
      <Grid item xs={6} sm={2}>
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_BLOQUE}
          label={t('form.field.bloque')}
          maxLength={5}
        />
      </Grid>
      <Grid item xs={6} sm={1.33}>
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_ESCALERA}
          label={t('form.field.escalera')}
          maxLength={2}
        />
      </Grid>
      <Grid item xs={6} sm={1.33}>
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_PISO}
          label={t('form.field.piso')}
          maxLength={3}
        />
      </Grid>
      <Grid item xs={6} sm={1.33}>
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_LETRA}
          label={t('form.field.letra')}
          maxLength={3}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <InputField
          id={FormField.INTERVINIENTE_DIRECCION_INFORMACION_COMPLEMENTARIA}
          label={t('form.field.informacion_complementaria_dirección')}
          maxLength={50}
        />
      </Grid>
    </Grid>
  )
}
